import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Row, Col, Card, CardHeader, CardBody,
  Spinner, Alert, Input, Button,
  Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "../../api/urls";
import useAnimatedPlaceholder, { SEARCH_FIELDS } from "../../hooks/useAnimatedPlaceholder";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import TruncatedText from "../../components/Common/TruncatedText";
import StatCards, { STAT_ACCENTS } from "../../components/Common/StatCards";
import { COMPANY_TYPES, COMPANY_TYPE_LABEL_MAP, COMPANY_TYPE_BADGE_COLORS } from "../../constants/companyTypes";

const WORKING_AREA_LABELS = {
  INFORMATION_TECHNOLOGY: "Bilişim ve Yazılım",
  MANUFACTURING: "Üretim ve İmalat",
  RETAIL: "Perakende Satış",
  HEALTHCARE: "Sağlık ve Tıp",
  FINANCE: "Finans ve Sigorta",
  EDUCATION: "Eğitim Hizmetleri",
  AUTOMOTIVE: "Otomotiv Sektörü",
  FOOD_AND_BEVERAGE: "Gıda ve İçecek",
  CONSTRUCTION: "İnşaat Sektörü",
  LOGISTICS: "Lojistik ve Nakliye",
  ENERGY: "Enerji",
  OTHER: "Diğer",
};

const TableCard = ({ className = "", children, ...rest }) => (
  <Card
    className={`rounded-4 border-0 shadow-lg ${className}`}
    style={{ boxShadow: "0 .5rem 1.25rem rgba(10,49,110,.08)" }}
    {...rest}
  >
    {children}
  </Card>
);

const useDebouncedValue = (value, delay = 500) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

const formatDateTR = (d) =>
  d ? new Date(d).toLocaleDateString("tr-TR", {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit"
  }) : "-";

const NAVY = "#221C5D";
const ORANGE = "#E56A22";

const STATUS_CONFIG = {
  PENDING:  { label: "Beklemede", color: "#E56A22" },
  APPROVED: { label: "Onaylandı", color: "#71C29B" },
  REJECTED: { label: "Reddedildi", color: "#E07A7A" },
};

const EMPTY_SUMMARY = { total: 0, PENDING: 0, APPROVED: 0, REJECTED: 0 };

/**
 * Kart sayaçları. Durum filtresi backend'de mevcut olduğu için sayılar tek tek
 * sorgulanır; yalnızca `totalElements` okunduğundan `size=1` yeterli, satır
 * gövdesi taşınmaz. Durum kırılımı anlamını korusun diye `status` filtresi
 * kasıtlı olarak uygulanmaz — diğer filtreler (arama, firma türü, faaliyet
 * alanı) listeyle birebir aynıdır.
 */
const fetchSummaryCounts = async (search = "", compType = "", workArea = "") => {
  const base = { page: 0, size: 1 };
  if (search) base.searchTerm = search;
  if (compType) base.companyType = compType;
  if (workArea) base.workingArea = workArea;

  const countOf = async (status) => {
    const params = status ? { ...base, status } : base;
    const res = await axios.get(API.admin_registrations_list, { params });
    return res.data?.data?.totalElements ?? 0;
  };

  const [total, PENDING, APPROVED, REJECTED] = await Promise.all([
    countOf(), countOf("PENDING"), countOf("APPROVED"), countOf("REJECTED"),
  ]);

  return { total, PENDING, APPROVED, REJECTED };
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, color: "#B0B7C3" };
  return (
    <span
      className="badge border-0"
      style={{ borderRadius: 999, padding: ".35rem .6rem", backgroundColor: cfg.color, color: "#fff" }}
    >
      {cfg.label}
    </span>
  );
};

const PaginationBar = ({
  currentPage, totalPages, totalElements, pageSize,
  onPageChange, onPageSizeChange,
}) => {
  const getPages = () => {
    const arr = [];
    const max = 5;
    let start = Math.max(0, currentPage - Math.floor(max / 2));
    let end = Math.min(totalPages - 1, start + max - 1);
    if (end - start + 1 < max) start = Math.max(0, end - max + 1);
    for (let i = start; i <= end; i++) arr.push(i);
    return arr;
  };

  if (!totalElements) return null;

  return (
    <div className="d-flex justify-content-between align-items-center mt-3 pagination-bar">
      <div className="text-muted" style={{ fontSize: ".9rem" }}>
        {totalElements} kayıttan{" "}
        {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalElements)}{" "}
        arası gösteriliyor
      </div>
      <div className="d-flex align-items-center gap-3 flex-wrap justify-content-end">
        <div className="d-flex align-items-center gap-2 page-size-wrap">
          <span className="text-muted" style={{ fontSize: ".9rem" }}>Sayfa başına</span>
          <select
            className="form-select form-select-sm rounded-pill page-size-select"
            value={pageSize}
            onChange={(e) => onPageSizeChange(parseInt(e.target.value))}
          >
            {[10, 25, 50, 100].map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <nav>
          <ul className="pagination pagination-sm mb-0 corporate-pagination">
            <li className={`page-item ${currentPage === 0 ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 0}>
                <i className="bx bx-chevron-left" />
              </button>
            </li>
            {getPages().map(p => (
              <li key={p} className={`page-item ${currentPage === p ? "active" : ""}`}>
                <button className="page-link" onClick={() => onPageChange(p)}>{p + 1}</button>
              </li>
            ))}
            <li className={`page-item ${currentPage >= totalPages - 1 ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1}>
                <i className="bx bx-chevron-right" />
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
};

/* ────────────────────── Review Modal (Approve/Reject) ────────────────────── */
const ReviewModal = ({ isOpen, toggle, request, action, onConfirm }) => {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) setReason("");
  }, [isOpen]);

  if (!request) return null;

  const isApprove = action === "APPROVED";

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = { approved: isApprove };
      if (!isApprove && reason.trim()) {
        payload.rejectionReason = reason.trim();
      }
      const res = await axios.post(API.admin_registration_review(request.id), payload);
      if (res.data.statusCode === 200) {
        onConfirm({ success: true, message: res.data.message || (isApprove ? "Başvuru onaylandı. Firma ve kullanıcı oluşturuldu." : "Başvuru reddedildi.") });
      } else {
        onConfirm({ success: false, message: res.data.message || "İşlem başarısız oldu." });
      }
    } catch (err) {
      onConfirm({ success: false, message: err.response?.data?.message || "Bir hata oluştu." });
    } finally {
      setLoading(false);
      toggle();
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>
        {isApprove ? "Başvuruyu Onayla" : "Başvuruyu Reddet"}
      </ModalHeader>
      <ModalBody>
        <p>
          <strong>{request.companyName}</strong> ({request.email}) başvurusunu{" "}
          {isApprove ? "onaylamak" : "reddetmek"} istediğinizden emin misiniz?
        </p>
        {isApprove && (
          <Alert color="info" className="mb-0">
            <i className="bx bx-info-circle me-1" />
            Onay verildiğinde firma ve kullanıcı hesabı otomatik oluşturulacak ve
            hoşgeldiniz e-postası gönderilecektir.
          </Alert>
        )}
        {!isApprove && (
          <div className="mt-3">
            <label className="form-label">Red Nedeni</label>
            <Input
              type="textarea"
              rows={3}
              placeholder="Red nedenini yazınız (opsiyonel)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={loading}>İptal</Button>
        <Button
          color={isApprove ? "success" : "danger"}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? <Spinner size="sm" /> : isApprove ? "Onayla" : "Reddet"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

/* ────────────────────── Main Table ────────────────────── */
const RegistrationRequestsTable = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, 500);
  const [statusFilter, setStatusFilter] = useState("");
  const [companyTypeFilter, setCompanyTypeFilter] = useState("");
  const [workingAreaFilter, setWorkingAreaFilter] = useState("");

  // Özet kartları — durum dışındaki filtrelerle aynı kümeden hesaplanır
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [summaryLoading, setSummaryLoading] = useState(true);

  // Sıralama (başvuru tarihi = createdAt) — null iken backend varsayılanı (createdAt desc)
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const [reviewRequest, setReviewRequest] = useState(null);
  const [reviewAction, setReviewAction] = useState("");
  const [reviewModalOpen, setReviewModalOpen] = useState(false);

  const navigate = useNavigate();
  const searchRef = useRef(null);
  const animatedPlaceholder = useAnimatedPlaceholder(SEARCH_FIELDS.registrations, { prefix: "Ara: " });

  const styles = `
    .reg-modern .table thead tr { background-color: #f9f9f9; }
    .reg-modern .table th { border: 0; color: #6c757d; font-weight: 600; }
    .reg-modern .table td { vertical-align: middle; }
    .reg-modern .table-hover tbody tr:hover { background: #fcfcfd; }
    .reg-modern .btn-rounded { border-radius: 999px; }
    .reg-modern .btn-navy { background: ${NAVY}; border-color: ${NAVY}; color: #fff; }
    .reg-modern .btn-navy:hover { filter: brightness(1.03); }
    .reg-modern .btn-orange { background: ${ORANGE}; border-color: ${ORANGE}; color: #fff; }
    .reg-modern .btn-orange:hover { filter: brightness(1.03); }
    .reg-modern .btn-green { background: #71C29B; border-color: #71C29B; color: #fff; }
    .reg-modern .btn-green:hover { filter: brightness(1.03); }
    .reg-modern .btn-red { background: #dc3545; border-color: #dc3545; color: #fff; }
    .reg-modern .btn-red:hover { filter: brightness(1.03); }
    .reg-modern .action-btn {
      width: 32px; height: 32px; padding: 0; border-radius: 50%; flex: 0 0 auto;
      display: inline-flex; align-items: center; justify-content: center;
    }
    .reg-modern .action-btn i { font-size: 1rem; line-height: 1; }
    .reg-modern .corporate-pagination .page-link { color: ${NAVY}; border-color: ${ORANGE}; }
    .reg-modern .corporate-pagination .page-item.active .page-link {
      background-color: ${NAVY}; border-color: ${NAVY}; color: #fff;
    }
    .reg-modern .corporate-pagination .page-link:hover { border-color: ${NAVY}; color: ${NAVY}; }
    .reg-modern .pill-input, .reg-modern .pill-select {
      border-radius: 999px !important;
      padding: .45rem .9rem;
    }
    .reg-modern .header-filters {
      display: flex; flex-wrap: wrap; gap: .5rem; align-items: center; justify-content: flex-end;
    }
    .reg-modern .header-filter-select {
      min-width: 140px; flex: 1 1 140px; max-width: 200px;
    }
    .reg-modern .search-input {
      min-width: 160px; flex: 2 1 180px; max-width: 260px;
    }
    .reg-modern .page-size-wrap { white-space: nowrap; }
    .reg-modern .page-size-select { min-width: 70px; padding-right: 1.75rem; }
    .reg-modern .pagination-bar { flex-wrap: wrap; gap: .5rem; }
    @media (max-width: 1280px) {
      .reg-modern .header-filter-select { max-width: 180px; }
      .reg-modern .search-input { max-width: 200px; }
    }
    @media (max-width: 992px) {
      .reg-modern .search-input { max-width: 100%; flex: 1 1 160px; }
      .reg-modern .pagination-bar { justify-content: flex-start !important; }
    }
    @media (max-width: 576px) {
      .reg-modern .table { font-size: 12px; }
      .reg-modern .table td, .reg-modern .table th { padding: .5rem; }
      .reg-modern .header-filter-select { max-width: 100%; flex: 1 1 100%; }
      .reg-modern .search-input { max-width: 100%; }
    }
  `;

  document.title = "Kayıt Talepleri | Tahsilat360";

  const fetchRequests = async (page = 0, size = 10, search = "", status = "", compType = "", workArea = "", first = false, sBy = sortBy, sDir = sortDir) => {
    try {
      setError(null);
      if (first) setLoading(true); else setRefreshing(true);

      const params = { page, size };
      if (search) params.searchTerm = search;
      if (status) params.status = status;
      if (compType) params.companyType = compType;
      if (workArea) params.workingArea = workArea;
      if (sBy) { params.sortBy = sBy; params.sortDir = sDir; }

      const res = await axios.get(API.admin_registrations_list, { params });
      const { content, totalElements, totalPages, page: pageIdx, size: sz } = res.data.data;
      setRequests(content);
      setTotalElements(totalElements);
      setTotalPages(totalPages);
      setCurrentPage(pageIdx);
      setPageSize(sz);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) return navigate("/login");
      setError(err.response?.data?.message || "Kayıt talepleri getirilirken bir hata oluştu.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Kart hatası listeyi etkilemez: Alert göstermez, sayaçlar sıfırlanır
  const refreshSummary = async (search = "", compType = "", workArea = "") => {
    setSummaryLoading(true);
    try {
      setSummary(await fetchSummaryCounts(search, compType, workArea));
    } catch {
      setSummary(EMPTY_SUMMARY);
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests(0, pageSize, "", statusFilter, companyTypeFilter, workingAreaFilter, true);
  }, []);

  // İlk yüklemede de çalışır; özet ayrıca burada tetiklenmez ki istek ikiye katlanmasın
  useEffect(() => {
    fetchRequests(0, pageSize, debouncedSearch, statusFilter, companyTypeFilter, workingAreaFilter);
    refreshSummary(debouncedSearch, companyTypeFilter, workingAreaFilter);
  }, [debouncedSearch]);

  // Durum filtresi kartları etkilemez (kartlar durum kırılımını gösterir)
  const handleStatusChange = (status) => {
    setStatusFilter(status);
    setCurrentPage(0);
    fetchRequests(0, pageSize, debouncedSearch, status, companyTypeFilter, workingAreaFilter);
  };

  const handleCompanyTypeChange = (type) => {
    setCompanyTypeFilter(type);
    setCurrentPage(0);
    fetchRequests(0, pageSize, debouncedSearch, statusFilter, type, workingAreaFilter);
    refreshSummary(debouncedSearch, type, workingAreaFilter);
  };

  const handleWorkingAreaChange = (area) => {
    setWorkingAreaFilter(area);
    setCurrentPage(0);
    fetchRequests(0, pageSize, debouncedSearch, statusFilter, companyTypeFilter, area);
    refreshSummary(debouncedSearch, companyTypeFilter, area);
  };

  const handlePageChange = (p) => {
    if (p >= 0 && p < totalPages) {
      setCurrentPage(p);
      fetchRequests(p, pageSize, debouncedSearch, statusFilter, companyTypeFilter, workingAreaFilter);
    }
  };

  const handlePageSizeChange = (sz) => {
    setPageSize(sz);
    setCurrentPage(0);
    fetchRequests(0, sz, debouncedSearch, statusFilter, companyTypeFilter, workingAreaFilter);
  };

  const handleSort = (field) => {
    const nextDir = sortBy === field ? (sortDir === "asc" ? "desc" : "asc") : "asc";
    setSortBy(field);
    setSortDir(nextDir);
    setCurrentPage(0);
    fetchRequests(0, pageSize, debouncedSearch, statusFilter, companyTypeFilter, workingAreaFilter, false, field, nextDir);
  };

  const sortIcon = (field) =>
    sortBy === field
      ? (sortDir === "asc" ? "bx bx-sort-up" : "bx bx-sort-down")
      : "bx bx-sort-alt-2";

  const refreshCurrent = () => {
    fetchRequests(currentPage, pageSize, debouncedSearch, statusFilter, companyTypeFilter, workingAreaFilter);
    refreshSummary(debouncedSearch, companyTypeFilter, workingAreaFilter);
  };

  // Tıklanan kart durum filtresini ayarlar; seçili karta tekrar tıklamak filtreyi kaldırır
  const summaryItems = useMemo(() => {
    const toggleStatus = (s) => handleStatusChange(statusFilter === s ? "" : s);

    return [
      {
        key: "total", label: "Toplam Başvuru", value: summary.total, hint: "Tüm talepler",
        accent: STAT_ACCENTS.navy, active: statusFilter === "",
        title: "Tüm başvuruları göster", onClick: () => handleStatusChange(""),
      },
      {
        key: "PENDING", label: "Beklemede", value: summary.PENDING, hint: "İnceleme bekliyor",
        accent: STAT_ACCENTS.amber, active: statusFilter === "PENDING",
        title: "Bekleyen başvuruları göster", onClick: () => toggleStatus("PENDING"),
      },
      {
        key: "APPROVED", label: "Onaylandı", value: summary.APPROVED, hint: "Aktif firmalar",
        accent: STAT_ACCENTS.green, active: statusFilter === "APPROVED",
        title: "Onaylanan başvuruları göster", onClick: () => toggleStatus("APPROVED"),
      },
      {
        key: "REJECTED", label: "Reddedildi", value: summary.REJECTED, hint: "Onaylanmadı",
        accent: STAT_ACCENTS.red, active: statusFilter === "REJECTED",
        title: "Reddedilen başvuruları göster", onClick: () => toggleStatus("REJECTED"),
      },
    ];
  }, [summary, statusFilter, debouncedSearch, companyTypeFilter, workingAreaFilter, pageSize]);

  const openReview = (req, action) => {
    setReviewRequest(req);
    setReviewAction(action);
    setReviewModalOpen(true);
  };

  return (
    <div className="page-content reg-modern">
      <style>{styles}</style>
      <div className="container-fluid">
        <StatCards className="mb-3" loading={summaryLoading} items={summaryItems} />

        <Row>
          <Col style={{ minWidth: 0 }}>
            <TableCard>
              <CardHeader className="bg-white border-0 rounded-top-4">
                {/* Desktop header */}
                <div className="d-none d-md-block">
                  <div className="mb-3">
                    <h5 className="mb-1 fw-bold text-dark">Kayıt Talepleri</h5>
                    <p className="mb-0 text-muted" style={{ fontSize: ".9rem" }}>
                      {totalElements > 0 ? `Toplam ${totalElements} başvuru bulundu` : "Başvuru bulunamadı"}
                    </p>
                  </div>

                  <div className="header-filters">
                    <select
                      className="form-select pill-select header-filter-select"
                      value={statusFilter}
                      onChange={(e) => handleStatusChange(e.target.value)}
                    >
                      <option value="">Tüm Durumlar</option>
                      <option value="PENDING">Beklemede</option>
                      <option value="APPROVED">Onaylandi</option>
                      <option value="REJECTED">Reddedildi</option>
                    </select>

                    <select
                      className="form-select pill-select header-filter-select"
                      value={companyTypeFilter}
                      onChange={(e) => handleCompanyTypeChange(e.target.value)}
                    >
                      <option value="">Tüm Firma Türleri</option>
                      {COMPANY_TYPES.map(ct => (
                        <option key={ct.value} value={ct.value}>{ct.label}</option>
                      ))}
                    </select>

                    <select
                      className="form-select pill-select header-filter-select"
                      value={workingAreaFilter}
                      onChange={(e) => handleWorkingAreaChange(e.target.value)}
                    >
                      <option value="">Tüm Faaliyet Alanları</option>
                      {Object.entries(WORKING_AREA_LABELS).map(([val, label]) => (
                        <option key={val} value={val}>{label}</option>
                      ))}
                    </select>

                    <Input
                      innerRef={searchRef}
                      type="search"
                      placeholder={animatedPlaceholder}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input pill-input"
                      inputMode="search"
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                  </div>
                </div>

                {/* Mobile header */}
                <div className="d-md-none">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0 fw-semibold">Kayıt Talepleri</h5>
                  </div>
                  <p className="mb-2 text-muted" style={{ fontSize: ".9rem" }}>
                    {totalElements > 0 ? `Toplam ${totalElements} başvuru` : "Başvuru bulunamadı"}
                  </p>
                  <div className="d-flex flex-column gap-2">
                    <Input
                      innerRef={searchRef}
                      type="search"
                      placeholder={animatedPlaceholder}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pill-input"
                      inputMode="search"
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                    <div className="d-flex gap-2 flex-wrap">
                      <select
                        className="form-select pill-select"
                        value={statusFilter}
                        onChange={(e) => handleStatusChange(e.target.value)}
                      >
                        <option value="">Tüm Durumlar</option>
                        <option value="PENDING">Beklemede</option>
                        <option value="APPROVED">Onaylandi</option>
                        <option value="REJECTED">Reddedildi</option>
                      </select>
                      <select
                        className="form-select pill-select"
                        value={companyTypeFilter}
                        onChange={(e) => handleCompanyTypeChange(e.target.value)}
                      >
                        <option value="">Tüm Firma Türleri</option>
                        {COMPANY_TYPES.map(ct => (
                          <option key={ct.value} value={ct.value}>{ct.label}</option>
                        ))}
                      </select>
                      <select
                        className="form-select pill-select"
                        value={workingAreaFilter}
                        onChange={(e) => handleWorkingAreaChange(e.target.value)}
                      >
                        <option value="">Tüm Faaliyet Alanları</option>
                        {Object.entries(WORKING_AREA_LABELS).map(([val, label]) => (
                          <option key={val} value={val}>{label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardBody className="pt-2">
                {error && (
                  <Alert color="danger" className="mb-3 rounded-4">
                    <i className="bx bx-error-circle me-2" />
                    {error}
                    <button
                      className="btn btn-sm btn-outline-danger ms-2 btn-rounded"
                      onClick={refreshCurrent}
                    >
                      Tekrar Dene
                    </button>
                  </Alert>
                )}

                {loading ? (
                  <div className="text-center p-5">
                    <Spinner size="lg" color="primary" />
                    <h6 className="mt-3 text-muted">Kayıt talepleri yükleniyor...</h6>
                  </div>
                ) : (
                  <>
                    <div className={`d-flex justify-content-between align-items-center mb-2 ${refreshing ? 'opacity-50' : ''}`}>
                      <div className="text-muted" style={{ fontSize: ".9rem" }}>
                        {totalElements > 0 && (
                          <>Toplam <strong>{totalElements}</strong> kayıt - Sayfa <strong>{currentPage + 1}</strong> / {totalPages}</>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <button
                          className="btn btn-light btn-sm btn-rounded"
                          onClick={refreshCurrent}
                          title="Yenile"
                        >
                          <i className={`bx bx-refresh ${refreshing ? 'bx-spin' : ''}`} />
                        </button>
                      </div>
                    </div>

                    <div className="table-responsive rounded-4 border">
                      <Table className="table table-hover mb-0">
                        <Thead>
                          <Tr>
                            <Th>Firma Adı</Th>
                            <Th className="d-none d-md-table-cell">Yetkili</Th>
                            <Th className="d-none d-sm-table-cell">E-posta</Th>
                            <Th className="d-none d-xl-table-cell">Telefon</Th>
                            <Th className="d-none d-xxl-table-cell">Firma Türü</Th>
                            <Th>Durum</Th>
                            <Th
                              className="d-none d-lg-table-cell"
                              style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                              onClick={() => handleSort("createdAt")}
                              title="Başvuru tarihine göre sırala"
                            >
                              Başvuru Tarihi <i className={sortIcon("createdAt")} />
                            </Th>
                            <Th className="text-end">İşlemler</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {requests.length ? requests.map(r => (
                            <Tr key={r.id} className="border-bottom">
                              <Td>
                                <TruncatedText
                                  text={r.companyName}
                                  maxLength={22}
                                  className="fw-semibold text-dark"
                                />
                              </Td>
                              <Td className="d-none d-md-table-cell">
                                <TruncatedText text={r.contactFullName} maxLength={20} />
                              </Td>
                              <Td className="d-none d-sm-table-cell">
                                <TruncatedText text={r.email} maxLength={22} />
                              </Td>
                              <Td className="d-none d-xl-table-cell">{r.phoneNumber || "-"}</Td>
                              <Td className="d-none d-xxl-table-cell">
                                {r.companyType && (
                                  <span
                                    className="badge border-0"
                                    style={{
                                      borderRadius: 999,
                                      padding: ".35rem .5rem",
                                      backgroundColor: COMPANY_TYPE_BADGE_COLORS[r.companyType] || "#B0B7C3",
                                      color: "#fff",
                                    }}
                                  >
                                    {COMPANY_TYPE_LABEL_MAP[r.companyType] || r.companyType}
                                  </span>
                                )}
                              </Td>
                              <Td><StatusBadge status={r.status} /></Td>
                              <Td className="d-none d-lg-table-cell">{formatDateTR(r.createdAt)}</Td>
                              <Td className="text-end">
                                <div className="d-flex gap-1 justify-content-end flex-wrap">
                                  <button
                                    className="btn btn-sm action-btn btn-orange"
                                    title="Detay"
                                    onClick={() => navigate(`/registration-requests/${r.id}`)}
                                  >
                                    <i className="bx bx-show" />
                                  </button>
                                  {r.status === "PENDING" && (
                                    <>
                                      <button
                                        className="btn btn-sm action-btn btn-green"
                                        title="Onayla"
                                        onClick={() => openReview(r, "APPROVED")}
                                      >
                                        <i className="bx bx-check" />
                                      </button>
                                      <button
                                        className="btn btn-sm action-btn btn-red"
                                        title="Reddet"
                                        onClick={() => openReview(r, "REJECTED")}
                                      >
                                        <i className="bx bx-x" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </Td>
                            </Tr>
                          )) : (
                            <Tr>
                              <Td colSpan="8" className="text-center py-4">
                                <div className="text-muted">
                                  <i className="bx bx-file display-4 d-block mb-2" />
                                  Başvuru bulunamadı
                                  {searchTerm && (
                                    <div className="mt-2">
                                      <small>Arama teriminizi değiştirmeyi deneyin</small>
                                    </div>
                                  )}
                                </div>
                              </Td>
                            </Tr>
                          )}
                        </Tbody>
                      </Table>
                    </div>

                    <PaginationBar
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalElements={totalElements}
                      pageSize={pageSize}
                      onPageChange={handlePageChange}
                      onPageSizeChange={handlePageSizeChange}
                    />
                  </>
                )}
              </CardBody>

              <ReviewModal
                isOpen={reviewModalOpen}
                toggle={() => setReviewModalOpen(false)}
                request={reviewRequest}
                action={reviewAction}
                onConfirm={({ success, message }) => {
                  setReviewModalOpen(false);
                  (success ? toast.success : toast.error)(message);
                  refreshCurrent();
                }}
              />

              <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick pauseOnFocusLoss draggable pauseOnHover />
            </TableCard>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default RegistrationRequestsTable;
